import { Data, Effect, Match, Option } from 'effect';

import { requiresNewToken } from '../../data/error/orderViolation';
import { OrderToken } from '../../service/orderToken/OrderToken';
import type { SetOrderTokenError } from '../../service/orderToken/OrderToken';
import { Cart } from './Cart';
import { CartError, CartReadAfterWriteError } from './errors';
import type { CartUnreadable, CartWriteError } from './errors';

/**
 * The order token's lifecycle, against the cart it addresses.
 *
 * {@link OrderToken} says where a storefront keeps the token and nothing else:
 * get it, set it, remove it. One invariant sits above those three verbs —
 * **the token in the store names an order the shopper can still write to** —
 * and it has no owner in that service, because deciding it needs the cart.
 * This module is that owner, and it is in the engine rather than in a
 * storefront because the invariant belongs to the port: any tenant that
 * provides {@link Cart} has the same token with the same three states.
 *
 * The three states are what the storage service cannot express. A token is
 * absent, or it is held and live, or it is held and **spent** — the order it
 * names is finished, expired, or closed, and every write against it is refused
 * for as long as the store keeps it. A spent token is indistinguishable from a
 * live one until something is written, which is why the decision lives beside
 * the write and not beside the cookie.
 *
 * ## What proves a token is spent
 *
 * Two answers, and the second is the one that matters on a backend that sends
 * no codes.
 *
 * - **The refusal names a code that says so.** `requiresNewTokenViolationCodes`
 *   is that list, and it is the engine's, so this module reads it rather than
 *   restating it. A backend that files its refusals under codes is answered
 *   without a second request.
 * - **The cart itself is gone.** A write refused against an order the shop no
 *   longer serves is a spent token whatever the refusal said, and a completed
 *   Sylius order is exactly that: `POST .../items` answers 422 with a sentence
 *   and `GET .../expand` answers 404. The read is the evidence, and it is
 *   evidence every backend produces.
 *
 * A `NotFound` from the write goes through the same read, and for a different
 * reason: `DELETE .../items/{id}` answers 404 both for a line that is not there
 * and for a cart that is not there. Only the cart's own answer tells the two
 * apart, and getting it wrong throws away a live cart over a stale line id.
 *
 * ## What a write costs
 *
 * A refused or missing write now makes one extra request. That is the same
 * number a successful write already makes — every cart write re-reads the cart
 * it changed — so the cost is symmetry rather than a new class of traffic.
 *
 * Cart reads use `readCurrentCart`. The caller supplies its stale token policy.
 */

/**
 * The token the store holds, if it holds one.
 *
 * A store that cannot be read answers `None` rather than failing. That is the
 * prerender case, where there is no request to read a cookie from and an empty
 * cart is the honest page.
 */
export const heldToken: Effect.Effect<Option.Option<string>, never, OrderToken> = Effect.gen(
  function* () {
    const orderToken = yield* OrderToken;

    return yield* orderToken.getToken.pipe(
      Effect.tapError((error) =>
        Effect.logWarning('cartToken: the order token could not be read', error)
      ),
      Effect.orElseSucceed(() => Option.none<string>())
    );
  }
);

/**
 * Forget the token this store holds.
 *
 * A removal that fails is logged and passed over. Every caller reaches this
 * because it has already established the token is spent, and reporting a
 * storage problem in place of that would describe the wrong failure. The cost
 * of the token surviving is the state the shopper was already in.
 */
export const releaseToken: Effect.Effect<void, never, OrderToken> = Effect.gen(function* () {
  const orderToken = yield* OrderToken;

  yield* orderToken.removeToken.pipe(
    Effect.tapError((error) =>
      Effect.logError('cartToken: the spent order token could not be cleared', error)
    ),
    Effect.ignore
  );
});

/**
 * A cart started now, with its token written down.
 *
 * A token that cannot be stored is a cart the shopper loses on the next request.
 * The storage failure therefore travels to the caller.
 */
export const startCart: Effect.Effect<
  string,
  CartWriteError | SetOrderTokenError,
  Cart | OrderToken
> = Effect.gen(function* () {
  const cart = yield* Cart;
  const orderToken = yield* OrderToken;

  const created = yield* cart.create.pipe(
    Effect.catch((error) =>
      Option.match(committedCartToken(error), {
        onNone: () => Effect.fail(error),
        onSome: (token) =>
          orderToken.setToken(token).pipe(
            Effect.ignore,
            Effect.andThen(
              Effect.fail(
                new CartError.Unavailable({
                  operation: 'create',
                  reference: Option.some(token),
                  cause: new CartStartedUnreadError({ cause: error }),
                })
              )
            )
          ),
      })
    )
  );

  yield* orderToken.setToken(created.token);

  return created.token;
});

/**
 * The shop created a cart, and its first read failed. The token is stored.
 * The write that needed the cart was never sent, so its failure is definite.
 */
class CartStartedUnreadError extends Data.TaggedError('@engine/Cart/StartedUnread')<{
  readonly cause: CartWriteError;
}> {}

/**
 * The token of a cart that the shop created although `create` failed.
 *
 * `create` fails after the shop committed the cart when the read that shows
 * the new cart fails. That failure names the new token. Storing it keeps the
 * shopper on that cart instead of leaving it orphaned.
 */
const committedCartToken = (error: CartWriteError): Option.Option<string> =>
  (error instanceof CartError.Unavailable && error.cause instanceof CartReadAfterWriteError) ||
  (error instanceof CartError.ContractMismatch && error.operation === 'create')
    ? error.reference
    : Option.none();

/**
 * The token to write into: the one the store holds, or a new cart's.
 *
 * **Only a write mints a cart.** A visitor with no token has an empty cart, and
 * the first thing they add is what starts one.
 */
export const tokenToWriteInto: Effect.Effect<
  string,
  CartWriteError | SetOrderTokenError,
  Cart | OrderToken
> = heldToken.pipe(
  Effect.flatMap(Option.match({ onNone: () => startCart, onSome: Effect.succeed }))
);

/**
 * Whether the shop still has the order this token names.
 *
 * A read that fails for any other reason travels rather than answering, so a
 * probe that could not reach the shop is never read as "the cart is gone".
 */
const cartIsGone = (token: string): Effect.Effect<boolean, CartUnreadable, Cart> =>
  Cart.pipe(
    Effect.flatMap((cart) => cart.cart(token)),
    Effect.as(false),
    Effect.catchTag(CartError.Tags.NotFound, () => Effect.succeed(true))
  );

/**
 * Whether this failed attempt proves the token names an order that can no
 * longer be written to.
 *
 * `spentOn` goes first. A caller names a failure there that proves its own
 * write can never succeed against this cart. `Rejected` short-circuits on the
 * codes and falls back to the cart. A probe that cannot read the cart then
 * answers "not spent", so the refusal keeps the shop's sentences. `NotFound`
 * always reads the cart, because the write's own 404 does not say whether the
 * line or the cart is what is missing; a probe failure travels in its place.
 * Every other failure is about the request rather than the order, and answers
 * `false`.
 */
const tokenIsSpent =
  (token: string, spentOn: (error: CartWriteError) => boolean) =>
  (error: CartWriteError): Effect.Effect<boolean, CartUnreadable, Cart> =>
    spentOn(error)
      ? Effect.succeed(true)
      : Match.value(error).pipe(
          Match.tag(CartError.Tags.NotFound, () => cartIsGone(token)),
          Match.tag(CartError.Tags.Rejected, ({ codes }) =>
            requiresNewToken(codes)
              ? Effect.succeed(true)
              : cartIsGone(token).pipe(
                  Effect.tapError((probe) =>
                    Effect.logWarning('cartToken: the spent-token probe could not read', probe)
                  ),
                  Effect.orElseSucceed(() => false)
                )
          ),
          Match.orElse(() => Effect.succeed(false))
        );

export type WriteIntoCartOptions = {
  /**
   * A failure that proves this write can never succeed against the held
   * cart, although the cart still reads. The token is then released, and the
   * write runs once against a new cart. Only a write that a new cart can take,
   * such as an add, names one.
   */
  readonly spentOn?: (error: CartWriteError) => boolean;
};

/**
 * One write against the cart this session addresses, on a token that is live.
 *
 * The whole lifecycle in one call: take the held token or start a cart, run the
 * write, and where the attempt proves the token spent, forget it, start a cart
 * and run the write once against that.
 *
 * **Exactly one retry.** A second failure against a cart minted moments ago is
 * not a stale token, and retrying it would hide a shop that is refusing the
 * write on its merits.
 *
 * `operation` answers the cart the shop states afterwards, which is the port's
 * promise — so the value this answers is what the shopper's page renders, from
 * whichever attempt succeeded.
 */
export const writeIntoCart = <A>(
  operation: (token: string) => Effect.Effect<A, CartWriteError, Cart>,
  options: WriteIntoCartOptions = {}
): Effect.Effect<A, CartWriteError | SetOrderTokenError, Cart | OrderToken> =>
  tokenToWriteInto.pipe(
    Effect.flatMap((token) =>
      operation(token).pipe(
        Effect.catch((error) =>
          tokenIsSpent(
            token,
            options.spentOn ?? (() => false)
          )(error).pipe(
            Effect.flatMap((spent) =>
              spent
                ? releaseToken.pipe(
                    Effect.flatMap(() => startCart),
                    Effect.flatMap(operation)
                  )
                : Effect.fail(error)
            )
          )
        )
      )
    )
  );
